import { z } from "zod";
import { type TimestampShared } from "../types/functions/utils/sharedDataTypes";
import { MediaType } from "../types/firestore/collections";
import { type ActivityLog, type ContentActivityLog } from "../types/firestore/app/users/activityLog/activityLog";

const timestampSharedSchema = z.custom<TimestampShared>(
  (val): val is TimestampShared =>
    typeof val === "object" &&
    val !== null &&
    "toMillis" in val &&
    typeof (val as { toMillis: unknown }).toMillis === "function",
);

const optionalProvenanceFields = {
  pointsEarned: z.number().int().min(0).optional(),
  recordedAt: timestampSharedSchema.optional(),
};

const silentMeditationActivityLogSchema = z.object({
  mediaType: z.literal(MediaType.SilentMeditation),
  durationInSeconds: z.number(),
  timestamp: timestampSharedSchema,
  ...optionalProvenanceFields,
});

const contentActivityLogSchema = z
  .object({
    mediaType: z.union([
      z.literal(MediaType.AudioCourses),
      z.literal(MediaType.IntroCourses),
      z.literal(MediaType.MeditationDailies),
    ]),
    mediaId: z.string(),
    timestamp: timestampSharedSchema,
    fileAsKebabCase: z.string().optional(),
    ...optionalProvenanceFields,
  })
  .transform((data): ContentActivityLog => {
    const base: ContentActivityLog = {
      mediaId: data.mediaId,
      mediaType: data.mediaType,
      timestamp: data.timestamp,
    };
    if (data.pointsEarned !== undefined) {
      base.pointsEarned = data.pointsEarned;
    }
    if (data.recordedAt !== undefined) {
      base.recordedAt = data.recordedAt;
    }
    if (data.mediaType !== MediaType.MeditationDailies && data.fileAsKebabCase !== undefined) {
      return { ...base, fileAsKebabCase: data.fileAsKebabCase };
    }
    return base;
  });

/** Firestore `activityLog` subcollection document shape (client/admin Timestamp). */
export const activityLogFromFirestoreSchema = z.union([silentMeditationActivityLogSchema, contentActivityLogSchema]);

export const parseActivityLogFromFirestore = (data: unknown): ActivityLog | null => {
  const result = activityLogFromFirestoreSchema.safeParse(data);
  return result.success ? result.data : null;
};
